<template>
  <DialogTemplateToolkit
    data-qa="winner-reason-confirm"
    :dialog="dialogOpen"
    :title="$t('Tenders.WinnerReasonModalTitle')"
    @close="onCancel">
    <template #activator-btn>
      <sButton
        :class="activatorClass"
        data-qa="btn-checkWinner"
        @click="dialogOpen = true">
        {{ $t('Tenders.ConfirmSelection') }}
      </sButton>
    </template>
    <template #card-text>
      <div class="mb-4">
        {{ $t('Tenders.WinnerReasonModalText', { SupplierNames: supplierNamesText }) }}
      </div>
      <sForm
        ref="form"
        data-qa="winner-reason-confirm-form"
        label-position="top"
        :model="form"
        :rules="rules">
        <sFormItem :label="$t('Tenders.WinnerReasonTypeLabel')">
          <sSelect
            data-qa="winner-reason-confirm-type"
            :value.sync="reasonTypeModel">
            <sOption
              v-for="option in reasonOptions"
              :key="option.value"
              :data-qa="`winner-reason-confirm-type-${option.value}`"
              :label="option.label"
              :value="option.value"/>
          </sSelect>
        </sFormItem>
        <sFormItem prop="text">
          <sInput
            data-qa="winner-reason-confirm-text"
            max-rows="15"
            rows="5"
            show-word-limit
            type="textarea"
            :value.sync="form.text"/>
        </sFormItem>
      </sForm>
    </template>
    <template #first_btn>
      <sButton
        data-qa="winner-reason-confirm-apply"
        @click="onApply">
        {{ $t('Tenders.ConfirmSelection') }}
      </sButton>
    </template>
  </DialogTemplateToolkit>
</template>

<script>
import DialogTemplateToolkit from 'components/common/dialogs/DialogTemplateToolkit.vue'

import winnerReasonMixin from 'mixins/winnerReasonMixin.js'

/**
 * Модалка обґрунтування вибору переможця не за найкращою ціною.
 * Рендерити лише коли winnerReasonMixin.showWinnerReasonModal true — рішення "показувати
 * цей компонент чи стандартну кнопку підтвердження вибору" приймає батьківська сторінка
 * через v-if/v-else. Сама модалка не завершує тендер — вона лише збирає причину і
 * emit'ить її нагору через 'apply', далі батько веде той самий флоу, що й звичайна
 * кнопка підтвердження вибору (checkWinner, попередження про частковий вибір групи тощо).
 */

export default {
  name: 'modalWinnerReasonConfirm',
  mixins: [ winnerReasonMixin ],
  data() {
    return {
      dialogOpen: false,
      reasonType: null,
      form: {
        text: ''
      },
      rules: {
        text: [
          {
            type: 'string',
            trigger: 'change',
            transform(value) {
              return value ? value.trim() : value
            },
            validator: (rule, value, callback) => {
              if (!value) {
                callback(new Error(this.$t('Validate.SpecifyWinnerReason')))
                return
              }
              if (value.length < 10) {
                callback(new Error(this.$t('Validate.minLength', { number: 10, type: this.$tc('Common.characters', 10) })))
                return
              }
              if (value.split(/\s+/).filter(Boolean).length < 2) {
                callback(new Error(this.$t('Validate.minLength', { number: 2, type: this.$tc('Common.words', 2) })))
                return
              }
              if (value.length > 4000) {
                callback(new Error(this.$t('Validate.maxLength', { number: 4000, type: this.$tc('Common.characters', 4000) })))
                return
              }
              callback()
            }
          }
        ]
      }
    }
  },
  methods: {
    validate() {
      return new Promise(resolve => {
        this.$refs.form.validate(valid => resolve(valid))
      })
    },
    onCancel() {
      this.form.text = ''
      this.reasonType = null
      this.dialogOpen = false
    },
    async onApply() {
      const valid = await this.validate()
      if (!valid) { return }
      this.$emit('apply', this.form.text)
      this.form.text = ''
      this.reasonType = null
      this.dialogOpen = false
    }
  },
  computed: {
    reasonOptions() {
      return [
        { value: 'BETTER_TECHNICAL_CHARACTERISTICS', label: this.$t('Tenders.WinnerReasonBetterTechnicalCharacteristics') },
        { value: 'FULL_COMPLIANCE_WITH_REQUIREMENTS', label: this.$t('Tenders.WinnerReasonFullComplianceWithRequirements') },
        { value: 'SUPPLIER_EXPERIENCE_AND_REPUTATION', label: this.$t('Tenders.WinnerReasonSupplierExperienceAndReputation') },
        { value: 'BETTER_DELIVERY_TERMS', label: this.$t('Tenders.WinnerReasonBetterDeliveryTerms') },
        { value: 'BETTER_PAYMENT_TERMS', label: this.$t('Tenders.WinnerReasonBetterPaymentTerms') },
        { value: 'OTHER', label: this.$t('Tenders.WinnerReasonOther') }
      ]
    },
    supplierNamesText() {
      const names = this.$_nonBestPriceWinnerNames(this.tender)
      const shown = names.slice(0, 3).join(', ')
      const rest = names.length - 3
      return rest > 0 ? `${shown} ${this.$t('Tenders.WinnerReasonAndMore', { count: rest })}` : shown
    },
    reasonTypeModel: {
      get() {
        return this.reasonType
      },
      set(value) {
        this.reasonType = value
        if (value === 'OTHER') { return }
        const label = this.reasonOptions.find(option => option.value === value)?.label
        if (!label) { return }
        this.form.text = this.form.text ? `${this.form.text}\n${label}` : label
      }
    }
  },
  components: {
    DialogTemplateToolkit
  },
  props: {
    tender: Object,
    activatorClass: [String, Object, Array]
  }
}
</script>
